import React, { useMemo, useState } from 'react';
import { apiGet, apiPost } from '../../api';
import { useAsync } from '../../hooks/useAsync';
import type { Company, CompanyType, FundraisingStage, Probability } from '../../types';
import { companyTypeLabel, fundraisingStageLabel, probabilityLabel } from '../../i18n';
import { useToast } from '../../components/ToastHost';

const STAGES: FundraisingStage[] = [
  'Mapeados',
  'Contactados',
  'Envío Info Principal',
  '1ra Reunión',
  'Interesados en continuar',
  'Envio Data Room',
  'En Negociación',
  'En Term Sheet',
  'Ganado',
  'Proceso Abandonado',
];

const TYPES: CompanyType[] = ['Fondo', 'VC', 'Angel', 'Otro'];
const PROBS: Probability[] = ['Bajo', 'Medio', 'Alto'];

export default function AdminCompanies() {
  const { data, loading, error, setData } = useAsync<{ companies: Company[] }>(() => apiGet('/api/admin/companies'), []);
  const toast = useToast();
  const [name, setName] = useState('');
  const [type, setType] = useState<CompanyType>('VC');
  const [country, setCountry] = useState('');
  const [stage, setStage] = useState<FundraisingStage>('Mapeados');
  const [prob, setProb] = useState<Probability>('Medio');
  const [leadsRound, setLeadsRound] = useState(false);
  const [creating, setCreating] = useState(false);

  const list = useMemo(() => (data?.companies || []).slice().sort((a, b) => a.name.localeCompare(b.name)), [data]);

  const create = async () => {
    setCreating(true);
    try {
      const created = await apiPost<{ company: Company }>('/api/admin/companies', {
        name,
        type,
        country: country || null,
        stage,
        probability: prob,
        leadsRound,
      });
      setName('');
      setLeadsRound(false);
      const updated = await apiGet<{ companies: Company[] }>('/api/admin/companies');
      setData(updated);
      toast.success('Investor created.');
      return created.company;
    } catch (e: any) {
      toast.error(e?.payload?.error || e?.message || 'Could not create investor.');
      throw e;
    } finally {
      setCreating(false);
    }
  };

  if (loading) return <div className="card">Loading…</div>;
  if (error) return <div className="card">Error: {error} (check Basic Auth)</div>;

  return (
    <div className="grid" style={{ gap: 14 }}>
      <div className="card">
        <div style={{ fontSize: 18, fontWeight: 900, marginBottom: 10 }}>New investor</div>
        <div className="row">
          <div>
            <div className="muted" style={{ fontSize: 12, marginBottom: 6 }}>
              Name
            </div>
            <input className="input" value={name} onChange={(e) => setName(e.target.value)} placeholder="Ex: Sequoia" />
          </div>
          <div>
            <div className="muted" style={{ fontSize: 12, marginBottom: 6 }}>
              Type
            </div>
            <select className="select" value={type} onChange={(e) => setType(e.target.value as CompanyType)}>
              {TYPES.map((t) => (
                <option key={t} value={t}>
                  {companyTypeLabel(t)}
                </option>
              ))}
            </select>
          </div>
          <div>
            <div className="muted" style={{ fontSize: 12, marginBottom: 6 }}>
              Country
            </div>
            <input className="input" value={country} onChange={(e) => setCountry(e.target.value)} placeholder="Ex: USA" />
          </div>
        </div>
        <div style={{ height: 10 }} />
        <div className="row">
          <div>
            <div className="muted" style={{ fontSize: 12, marginBottom: 6 }}>
              Stage
            </div>
            <select className="select" value={stage} onChange={(e) => setStage(e.target.value as FundraisingStage)}>
              {STAGES.map((s) => (
                <option key={s} value={s}>
                  {fundraisingStageLabel(s)}
                </option>
              ))}
            </select>
          </div>
          <div>
            <div className="muted" style={{ fontSize: 12, marginBottom: 6 }}>
              Probability
            </div>
            <select className="select" value={prob} onChange={(e) => setProb(e.target.value as Probability)}>
              {PROBS.map((p) => (
                <option key={p} value={p}>
                  {probabilityLabel(p)}
                </option>
              ))}
            </select>
          </div>
          <div>
            <div className="muted" style={{ fontSize: 12, marginBottom: 6 }}>
              Ronda
            </div>
            <label style={{ display: 'flex', gap: 10, alignItems: 'center', height: 40 }}>
              <input type="checkbox" checked={leadsRound} onChange={(e) => setLeadsRound(e.target.checked)} />
              <span>Lidera Ronda?</span>
            </label>
          </div>
          <div style={{ display: 'flex', alignItems: 'flex-end' }}>
            <button className="btn primary" disabled={!name.trim() || creating} onClick={create}>
              {creating ? 'Creating…' : 'Create'}
            </button>
          </div>
        </div>
      </div>

      <div className="card">
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
          <div style={{ fontSize: 18, fontWeight: 900 }}>Investors ({list.length})</div>
        </div>
        <div className="hr" />
        <table className="table">
          <thead>
            <tr>
              <th>Investor</th>
              <th>Type</th>
              <th>Stage</th>
              <th>Prob.</th>
              <th style={{ textAlign: 'right' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {list.map((c) => (
              <tr key={c.id}>
                <td>
                  <div style={{ fontWeight: 850 }}>{c.name}</div>
                  <div className="muted" style={{ fontSize: 12 }}>
                    {c.country || '—'}
                  </div>
                </td>
                <td className="muted">{companyTypeLabel(c.type)}</td>
                <td className="muted">{fundraisingStageLabel(c.stage)}</td>
                <td className="muted">{probabilityLabel(c.probability)}</td>
                <td style={{ textAlign: 'right' }}>
                  <a className="btn" href={`/admin/companies/${c.id}`}>
                    Open
                  </a>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
